import { HqlCode } from '@/components/hql/HqlCode';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { considerationTypesState, taskTypesByConsiderationTypeState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { HomeDocument, HqlCatalog } from '@rock/hql';
import type { SpaceId } from '@rock/types';
import type { FC } from 'react';
import { useMemo } from 'react';
import { type DryRunOutcome, describeCovers, dryRun } from './dryRun';

/**
 * Every approved query, and every draft awaiting review, run against this home: what each one matches
 * here. Pick a row to open its query in the box above.
 */
export const DryRunCard: FC<{
    doc: HomeDocument;
    catalog: HqlCatalog;
    onTryQuery: (query: string, spaceId: SpaceId | undefined) => void;
}> = ({ doc, catalog, onTryQuery }) => {
    const types = useAsyncAtomValue(considerationTypesState);
    const taskTypesByType = useAsyncAtomValue(taskTypesByConsiderationTypeState);
    const rows = useMemo(
        () => (types && taskTypesByType ? dryRun(types, taskTypesByType, doc, catalog, new Date()) : undefined),
        [types, taskTypesByType, doc, catalog],
    );
    return (
        <Card>
            <CardHeader>
                <CardTitle>Dry Run</CardTitle>
                <CardDescription>
                    Approved queries and drafts awaiting review, evaluated against this home. Nothing is written.
                </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                {!rows ? (
                    <div className="text-muted-foreground">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-muted-foreground">No approved or drafted queries yet.</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Consideration</TableHead>
                                <TableHead>Query</TableHead>
                                <TableHead>Here</TableHead>
                                <TableHead>Covers</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow
                                    key={row.key}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => onTryQuery(row.query, row.spaceId)}
                                >
                                    <TableCell className="font-medium">
                                        {row.type.title}
                                        {row.isDraft ? (
                                            <Badge
                                                variant="outline"
                                                className="ml-2 border-amber-300 bg-amber-50 text-amber-800"
                                            >
                                                Draft
                                            </Badge>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="max-w-md truncate">
                                        <HqlCode query={row.query} />
                                    </TableCell>
                                    <TableCell>
                                        <OutcomeBadge outcome={row.outcome} />
                                    </TableCell>
                                    <TableCell className="text-xs">{describeCovers(row.outcome, doc)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

const OutcomeBadge: FC<{ outcome: DryRunOutcome }> = ({ outcome }) => {
    if (outcome === 'invalid') return <Badge variant="destructive">Invalid</Badge>;
    if (outcome === 'mixed') return <Badge variant="destructive">Not applied: mixed</Badge>;
    if (outcome.status === 'applies') return <Badge variant="success">Applies</Badge>;
    if (outcome.status === 'pending') {
        return (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                Pending
            </Badge>
        );
    }
    return <Badge variant="secondary">Does not apply</Badge>;
};
